import { CircularProgress, Skeleton } from '@mui/material';
import { Product } from 'TYPES/api.body';
import { LikeIcon, LogoIcon } from 'assets/Icons';
import { ProductQtyCounter, Spacer } from 'components/atoms';
import SEARCH_PARAMS from 'constants/searchParams';
import renderIcon from 'icon';
import { addProductToCart, removeProductFromCart, updateCartItemQty } from 'network/mutations/products';
import { getProductDetail } from 'network/services/goods';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { HiOutlineShare } from 'react-icons/hi';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { IWishListItem, wishListState } from 'store/atoms/productDetail';
import { profileAtom } from 'store/atoms/profileAtom';
import { userRoleAtom } from 'store/atoms/userAtom';
import { formatCurrency } from 'utils/currencyFormater';
import { useUpdatedCart } from '../Cart/hooks';
import { Modal } from '../Modal/Modal';
import { AddToCart, GoodSDetails, ProductDsc, extraModalContentStyle } from './style';
import { performAddProductToCart } from './utils';

export const GoodsDetailModal = () => {
  const route = useNavigate();
  const location = useLocation();
  const serverCart = useUpdatedCart();
  const [searchParams, setSearchParams] = useSearchParams();
  const [productDetail, setProductDetail] = React.useState<Product | null>(location.state);
  const profile = useRecoilValue(profileAtom);
  const [wishList, setWishList] = useRecoilState(wishListState);
  const userRole = useRecoilValue(userRoleAtom);
  const [mainImg, setMainImg] = useState<string | string[]>();
  const [quantity, setQuantity] = useState(() => {
    return userRole === 'BUSINESS' ? productDetail?.minimumOrderQuantity ?? 1 : 1;
  });
  const wishitemIndex = wishList.findIndex(item => item.id === productDetail?.productId);
  const isItemInWishlist = wishitemIndex >= 0;
  const queryClient = useQueryClient();
  const { isLoading: detailLoading } = useQuery({
    queryFn: () => getProductDetail(parseInt(searchParams.get('productId') ?? '1')),
    onSuccess: data => {
      setProductDetail(data.data ?? null);

      // DEBUG LOGGING FOR IMAGES
      console.log('=== PRODUCT DETAIL DEBUG ===');
      console.log('Full product data:', data.data);
      console.log('Product images field:', data.data?.images);
      console.log('Product productImage1 field:', data.data?.productImage1);
      console.log('Product productImage2 field:', data.data?.productImage2);
      console.log('Product productImage3 field:', data.data?.productImage3);
      console.log('Product productImage4 field:', data.data?.productImage4);
      console.log('All product keys:', Object.keys(data.data || {}));
      console.log('=== END DEBUG ===');
    },
    onError: err => {
      enqueueSnackbar({ message: (err as any).message });
    }
  });

  const { mutateAsync: addMutate, error: addToCartError, isLoading } = useMutation({ mutationFn: addProductToCart });
  const { mutateAsync: updateMutate } = useMutation({
    mutationFn: updateCartItemQty
  });
  const { mutateAsync: removeMutate } = useMutation({
    mutationFn: removeProductFromCart
  });

  function findProductInCart(productId: number, cart: any) {
    // Ensure cart and orders are defined and orders is an array
    if (!cart || !Array.isArray(cart.orders)) {
      return { orderId: null, productFound: false };
    }

    // Iterate through all orders
    for (const order of cart.orders) {
      // Check if any product in this order matches the productId
      for (const product of order.products) {
        if (product.productId === productId) {
          return { orderId: order.id, productFound: true };
        }
      }
    }

    // If no product was found
    return { orderId: null, productFound: false };
  }
  const result =
    productDetail?.productId !== undefined
      ? findProductInCart(productDetail.productId, serverCart?.data)
      : { orderId: null, productFound: false };

  if (addToCartError) {
    enqueueSnackbar((addToCartError as any).message, { variant: 'error' });
  }

  const closeModal = () => {
    // If we have history to go back to, pop the modal entry instead of stacking duplicates
    if (searchParams.get('productId') && window.history.length > 1) {
      route(-1);
      return;
    }

    const search = new URLSearchParams(location.search);
    search.delete('productId');
    const path =
      Array.from(search.entries()).length > 0 ? location.pathname.concat('?', search.toString()) : location.pathname;
    route(path, { state: undefined, replace: true }); // replace prevents reopening modal on browser back
  };

  const toggleWishList = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.stopPropagation();
    if (isItemInWishlist) {
      const newArray = wishList.filter(item => item.id !== productDetail?.productId);
      setWishList(newArray);
    } else {
      setWishList(oldWishList => [
        ...oldWishList,
        {
          id: productDetail?.productId ?? 0,
          atrs: [],
          imgs: [
            productDetail?.productImage1,
            productDetail?.productImage2,
            productDetail?.productImage3,
            productDetail?.productImage4
          ].filter(Boolean), // Remove undefined values
          shortDSC: productDetail?.description ?? '',
          name: productDetail?.productName ?? '',
          img: productDetail?.productImage1 ?? '',
          amount: productDetail?.price ?? 0,
          isLiked: false
        } as IWishListItem // Ensure this is explicitly typed
      ]);
    }
  };

  const addToCartItem = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    if (profile === undefined) {
      route('/login');
      // localAddProductToCart(quantity, cartList, cartItem, productDetail?.seller?.id!, setCartList);
    } else {
      performAddProductToCart(
        serverCart?.data,
        productDetail?.productId,
        quantity,
        result?.orderId,
        addMutate,
        updateMutate,
        removeMutate,
        queryClient,
        'REGULAR'
      );
      closeModal();
    }
  };

  const addToCartItemForBusiness = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    if (profile === undefined) {
      // localAddProductToCart(quantity, cartList, cartItem, productDetail?.seller?.id!, setCartList);
      route('/login');
    } else {
      performAddProductToCart(
        serverCart?.data,
        productDetail?.productId,
        quantity,
        result?.orderId,
        addMutate,
        updateMutate,
        removeMutate,
        queryClient,
        'BUSINESS'
      );
      closeModal();
    }
  };

  useEffect(() => {
    if (productDetail) {
      // Build images array from available productImage fields
      const imageArray: string[] = [
        productDetail.productImage1,
        productDetail.productImage2,
        productDetail.productImage3,
        productDetail.productImage4
      ].filter((img): img is string => Boolean(img)); // Remove undefined/null values

      setMainImg(imageArray.length > 0 ? imageArray : undefined);

      // ADDITIONAL DEBUG LOGGING IN USEEFFECT
      console.log('=== USEEFFECT DEBUG ===');
      console.log('productDetail in useEffect:', productDetail);
      console.log('productDetail.productImage1:', productDetail.productImage1);
      console.log('productDetail.productImage2:', productDetail.productImage2);
      console.log('productDetail.productImage3:', productDetail.productImage3);
      console.log('productDetail.productImage4:', productDetail.productImage4);
      console.log('Built imageArray:', imageArray);
      console.log('Setting mainImg to:', imageArray.length > 0 ? imageArray : undefined);
      console.log('=== END USEEFFECT DEBUG ===');

      // Update quantity if productDetail changes and userRole is BUSINESS
      if (userRole === 'BUSINESS') {
        setQuantity(productDetail.minimumOrderQuantity ?? 1);
      }
    }
  }, [productDetail, userRole]);

  // Close modal on page reload - store intention in sessionStorage with multiple fallbacks
  useEffect(() => {
    const isModalOpen = searchParams.get('productId');

    if (isModalOpen) {
      const handleBeforeUnload = () => {
        // Mark that page is reloading while modal is open
        sessionStorage.setItem('closeModalOnReload', 'true');
        localStorage.setItem('closeModalOnReload', 'true');
        console.log('beforeunload: Set flags to close modal on reload');
      };

      const handleUnload = () => {
        // Extra safety: set again on unload
        sessionStorage.setItem('closeModalOnReload', 'true');
        localStorage.setItem('closeModalOnReload', 'true');
        console.log('unload: Set flags to close modal on reload');
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('unload', handleUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('unload', handleUnload);
      };
    }
  }, [searchParams]);

  // Check if we should close modal after reload
  useEffect(() => {
    const shouldClose = sessionStorage.getItem('closeModalOnReload') === 'true' ||
      localStorage.getItem('closeModalOnReload') === 'true';

    if (shouldClose) {
      console.log('Modal reload close triggered');
      sessionStorage.removeItem('closeModalOnReload');
      localStorage.removeItem('closeModalOnReload');
      closeModal();
    }
  }, []);

  // DEBUG LOGGING FOR MAINIMG STATE
  useEffect(() => {
    console.log('=== MAINIMG STATE DEBUG ===');
    console.log('mainImg state changed to:', mainImg);
    console.log('mainImg type:', typeof mainImg);
    console.log('mainImg is array?', Array.isArray(mainImg));
    if (Array.isArray(mainImg)) {
      console.log('mainImg array length:', mainImg.length);
      console.log('mainImg array contents:', mainImg);
    }
    console.log('=== END MAINIMG DEBUG ===');
  }, [mainImg]);

  const disabled =
    (quantity < (productDetail?.minimumOrderQuantity ?? 0) && userRole === 'BUSINESS') ||
    (productDetail === null && detailLoading);

  return (
    <Modal onModalClose={closeModal} extraModalContentStyle={extraModalContentStyle}>
      <GoodSDetails>
        <div className="right">
          <div className="imageCon">
            {mainImg ? <img src={Array.isArray(mainImg) ? mainImg[0] : mainImg} alt="productImage" /> : <LogoIcon />}
          </div>
          <Spacer height={20} />
          {/* productDetail.productImage1 */}
          <div className="flex gap-3 items-center">
            {Array.isArray(mainImg) && mainImg[1] && (
              <img src={mainImg[1]} width={90} height={90} alt="productImage" className="rounded-2xl" />
            )}
            {Array.isArray(mainImg) && mainImg[2] && (
              <img src={mainImg[2]} width={90} height={90} alt="productImage" className="rounded-2xl" />
            )}
            {Array.isArray(mainImg) && mainImg[3] && (
              <img src={mainImg[3]} width={90} height={90} alt="productImage" className="rounded-2xl" />
            )}
          </div>
        </div>
        <div className="left">
          {!productDetail && detailLoading ? (
            <Skeleton height={30} width={200} />
          ) : (
            <h1>{productDetail?.productName}</h1>
          )}
          <Spacer height={'1.2rem'} />

          {!productDetail && detailLoading ? (
            <Skeleton height={50} width={200} />
          ) : (
            <h4>{formatCurrency(productDetail?.price ?? 0)}</h4>
          )}
          <Spacer height={'1.2rem'} />
          <div>
            <p className="label">
              Quantity Available: <span>{productDetail?.quantityAvailable ?? 0}</span>
            </p>
            <Spacer height={'0.5rem'} />
            {userRole === 'BUSINESS' && (
              <div>
                <p className="label">
                  Minimum Order Quantity: <span>{productDetail?.minimumOrderQuantity ?? 0}</span>
                </p>
                <Spacer height={'0.5rem'} />

                <p className="label">
                  Bulk Order Price: <span>{formatCurrency(productDetail?.bulkOrderPrice ?? 0)}</span>
                </p>
                <Spacer height={'0.5rem'} />
              </div>
            )}

            <Spacer height={'1rem'} />
            <ProductQtyCounter
              max={productDetail?.quantityAvailable}
              customerType={userRole as any}
              value={quantity}
              minimumOrderQuantity={productDetail?.minimumOrderQuantity ?? 1}
              setValue={setQuantity}
            />
          </div>
          <Spacer height={'2rem'} />

          <AddToCart $isLiked={isItemInWishlist}>
            <button
              disabled={disabled}
              onClick={userRole === 'BUSINESS' ? addToCartItemForBusiness : addToCartItem}
              className="relative flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#0066FF] to-[#0052CC] text-white rounded-lg hover:from-[#0052CC] hover:to-[#0047B3] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg">
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Add to cart'}
            </button>
            <Spacer width={'1rem'} />
            <div className="likeCon">
              <LikeIcon onClick={toggleWishList} />
            </div>
            <button
              title={`Share ${productDetail?.productName}`}
              type="button"
              onClick={() =>
                setSearchParams(c => {
                  c.append(SEARCH_PARAMS.SHARE_URL, window.location.href);
                  return c;
                })
              }
              className="text-lg ml-2 p-2 rounded-full bg-black/10 hover:bg-black/20 border border-black/5 hover:border-black/20 transition-all">
              {renderIcon(HiOutlineShare)}
            </button>
          </AddToCart>
        </div>
      </GoodSDetails>
      <Spacer height={40} />
      {productDetail?.description && productDetail.description.length > 0 && (
        <ProductDsc>
          <h3>Product description :</h3>
          <Spacer height={10} />
          <div dangerouslySetInnerHTML={{ __html: productDetail?.description }} />
        </ProductDsc>
      )}
      <Spacer height={40} />
    </Modal>
  );
};
